import React, { useMemo, useState } from "react";
import { Check, CircleDot, X as XIcon, Loader2, ArrowRight, RotateCcw, ChevronRight } from "lucide-react";
import { getOccurrenceHistory, formatTime } from "@/utils";
import { api } from "@/api";
import { logDiag, rememberTraced, stateSummary } from "@/lib/diagnostics";
import { toast } from "sonner";

/**
 * OccurrenceHistoryList — per-occurrence log for a recurring task, rendered inside TaskEditor.
 * Shows the last 30 days + next 7 days of scheduled beats with each beat's state derived from
 * `completions[]`, `completion_times`, `instance_overrides[]` and the frozen legacy baseline
 * `completed_at`. The user can:
 *   · Mark a MISSED beat done → adds ISO to `completions[]` + records timestamp
 *   · Undo a COMPLETED beat  → removes ISO from `completions[]` + strips timestamp
 * PENDING / UPCOMING / POSTPONED beats are read-only status indicators (the row's own
 * checkbox and the Postpone button already handle those flows elsewhere).
 */

const isoToDayLabel = (iso, todayIso, tomorrowIso, yesterdayIso) => {
    if (iso === todayIso) return "Today";
    if (iso === tomorrowIso) return "Tomorrow";
    if (iso === yesterdayIso) return "Yesterday";
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
};

const stateStyle = {
    completed: { chip: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", label: "Completed" },
    pending:   { chip: "text-amber-300  border-amber-500/40  bg-amber-500/10",   label: "Pending"   },
    missed:    { chip: "text-red-300    border-red-500/40    bg-red-500/10",     label: "Missed"    },
    upcoming:  { chip: "text-meye-cyan  border-meye-cyan/40  bg-meye-cyan/10",   label: "Upcoming"  },
    postponed: { chip: "text-violet-300 border-violet-500/40 bg-violet-500/10",  label: "Postponed" },
    snoozed:   { chip: "text-amber-200  border-amber-400/40  bg-amber-400/10",   label: "Snoozed"   },
};

const fmtCompletionTime = (ms) => {
    if (!ms) return null;
    const d = new Date(ms);
    // "10:14 PM" — matches app's other time chips
    let h = d.getHours(), m = d.getMinutes();
    const suf = h >= 12 ? "PM" : "AM";
    h = h % 12; if (h === 0) h = 12;
    return `${h}:${String(m).padStart(2, "0")} ${suf}`;
};

export const OccurrenceHistoryList = ({ task, onTaskUpdated }) => {
    const [busyIso, setBusyIso] = useState(null);
    const [expanded, setExpanded] = useState(false); // collapsed by default — expand to inspect/correct beats
    const [localCompletions, setLocalCompletions] = useState(() => task?.completions || []);
    const [localCompletionTimes, setLocalCompletionTimes] = useState(() => task?.completion_times || {});

    const history = useMemo(() => {
        if (!task) return [];
        // Re-derive from local state so optimistic toggles reflect immediately.
        const scratch = { ...task, completions: localCompletions, completion_times: localCompletionTimes };
        return getOccurrenceHistory(scratch);
    }, [task, localCompletions, localCompletionTimes]);

    if (!task || task.type !== "recurring" || !task.interval) return null;

    const now = new Date();
    const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowIso = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
    const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayIso = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, "0")}-${String(yesterday.getDate()).padStart(2, "0")}`;

    const toggleBeat = async (iso, currentlyCompleted) => {
        setBusyIso(iso);
        const nextCompletions = currentlyCompleted
            ? localCompletions.filter((x) => x !== iso)
            : Array.from(new Set([...localCompletions, iso]));
        const nextTimes = { ...localCompletionTimes };
        if (currentlyCompleted) delete nextTimes[iso];
        else nextTimes[iso] = Date.now();
        try {
            const traceId = api.newTraceId();
            logDiag({ kind: "complete.click", task_id: task.id, iso, view: "history", trace_id: traceId, message: `"${String(task.text || "").slice(0, 60)}" ${currentlyCompleted ? "reopen" : "complete"}`, state: stateSummary(task) });
            rememberTraced(task.id, iso, task.text);
            const upd = currentlyCompleted ? await api.reopenOccurrence(task.id, iso, traceId) : await api.completeOccurrence(task.id, iso, traceId);
            logDiag({ kind: "complete.response", task_id: task.id, iso, trace_id: traceId, state: stateSummary(upd) });
            setLocalCompletions(upd.completions || nextCompletions);
            setLocalCompletionTimes(upd.completion_times || nextTimes);
            onTaskUpdated?.(upd);
        } catch {
            toast.error("Couldn't save — try again");
        } finally {
            setBusyIso(null);
        }
    };

    const missedCount = history.filter((r) => r.state === "missed").length;

    return (
        <div className="space-y-2 pt-3 border-t border-meye-border" data-testid="occurrence-history">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                className="w-full flex items-center justify-between gap-2 text-left group"
                data-testid="occurrence-history-toggle"
            >
                <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-mono group-hover:text-zinc-300 transition-colors">
                    <ChevronRight size={12} strokeWidth={2.5} className={`transition-transform duration-200 ${expanded ? "rotate-90" : ""}`} />
                    history · last 30 days
                </span>
                {missedCount > 0 && (
                    <span
                        className="text-[10px] font-brand font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-red-500/40 bg-red-500/10 text-red-300"
                        data-testid="history-missed-count"
                    >
                        {missedCount} outstanding
                    </span>
                )}
            </button>
            {expanded && (
            <div className="max-h-64 overflow-y-auto pr-1 space-y-1 rounded border border-meye-border bg-black/20 p-1.5" data-testid="occurrence-history-rows">
                {history.length === 0 && (
                    <p className="text-xs text-zinc-500 text-center py-3">No occurrences in this window.</p>
                )}
                {history.map((row) => {
                    const style = stateStyle[row.state] || stateStyle.upcoming;
                    const dayLabel = isoToDayLabel(row.displayIso || row.iso, todayIso, tomorrowIso, yesterdayIso);
                    const timeStr = row.time ? formatTime(row.time) : null; // effective time (to_time for moved beats)
                    const completedAtStr = row.state === "completed" ? fmtCompletionTime(row.beatMs && localCompletionTimes[row.iso]) : null;
                    const canToggle = row.state === "missed" || row.state === "completed";
                    const isCompleted = row.state === "completed";
                    return (
                        <div
                            key={row.key || row.sourceIso}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 transition-colors"
                            data-testid={`history-row-${row.iso}`}
                        >
                            {canToggle ? (
                                <button
                                    type="button"
                                    onClick={() => toggleBeat(row.iso, isCompleted)}
                                    disabled={busyIso === row.iso}
                                    className={`w-5 h-5 flex-shrink-0 rounded-md border flex items-center justify-center transition-colors ${
                                        isCompleted
                                            ? "bg-emerald-500 border-emerald-500 text-black"
                                            : "border-zinc-600 hover:border-meye-cyan"
                                    }`}
                                    title={isCompleted ? "Undo — mark as missed again" : "Mark this beat done"}
                                    data-testid={`history-toggle-${row.iso}`}
                                >
                                    {busyIso === row.iso ? (
                                        <Loader2 size={12} className="animate-spin" />
                                    ) : isCompleted ? (
                                        <Check size={12} strokeWidth={3} />
                                    ) : null}
                                </button>
                            ) : (
                                <span
                                    className="w-5 h-5 flex-shrink-0 rounded-md border border-zinc-800 flex items-center justify-center"
                                    aria-hidden="true"
                                >
                                    {row.state === "pending"   && <CircleDot size={12} className="text-amber-300" />}
                                    {row.state === "postponed" && <ArrowRight size={12} className="text-violet-300" />}
                                    {row.state === "upcoming"  && <CircleDot size={10} className="text-meye-cyan" />}
                                </span>
                            )}

                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-zinc-100">
                                    {dayLabel}
                                    {timeStr && <span className="text-zinc-500 ml-1">· {timeStr}</span>}
                                </div>
                                {(row.movedTo || row.movedFrom) && (
                                    <div className="text-[10px] text-violet-300 uppercase tracking-widest">
                                        {row.movedTo ? `moved to ${row.movedTo}` : `moved from ${row.movedFrom}`}
                                    </div>
                                )}
                            </div>

                            <span
                                className={`text-[10px] font-brand font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${style.chip}`}
                                data-testid={`history-state-${row.iso}`}
                            >
                                {style.label}
                                {completedAtStr && <span className="ml-1 opacity-80">· {completedAtStr}</span>}
                            </span>

                            {row.state === "completed" && (
                                <button
                                    type="button"
                                    onClick={() => toggleBeat(row.iso, true)}
                                    disabled={busyIso === row.iso}
                                    className="text-zinc-500 hover:text-red-300 flex-shrink-0"
                                    title="Undo this completion"
                                    data-testid={`history-undo-${row.iso}`}
                                >
                                    <RotateCcw size={12} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            )}
        </div>
    );
};

export default OccurrenceHistoryList;
